import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Palette, LayoutGrid, Bell, RefreshCcw, ShieldCheck, Users, Info } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SettingsLayout } from '@/components/settings/SettingsLayout';
import { useUserStore } from '@/stores/userStore';
import { useTheme } from '@/hooks/useTheme';
import { useProjectStore, type StageType } from '@/stores/projectStore';
import { cn } from '@/lib/utils';

function usePersistentBoolean(key: string, defaultValue: boolean): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultValue;
    const raw = window.localStorage.getItem(key);
    return raw === null ? defaultValue : raw === 'true';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, value ? 'true' : 'false');
    }
  }, [key, value]);

  return [value, setValue];
}

const stageOptions: Array<{ id: StageType; label: string; description: string }> = [
  { id: 'PLAN', label: 'Agent Plan', description: 'Blueprint first' },
  { id: 'LUMASCRIPT', label: 'LumaScript', description: 'Jump into flows' },
  { id: 'BPMN', label: 'BPMN Preview', description: 'See orchestration first' },
  { id: 'ARTIFACT_DESIGN', label: 'Artifact Design', description: 'Start at artifacts' }
];

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useUserStore();
  const { theme, setTheme } = useTheme();
  const explorerGroupBy = useProjectStore((state) => state.ui.explorerGroupBy);
  const setExplorerGroupBy = useProjectStore((state) => state.setExplorerGroupBy);
  const activeStage = useProjectStore((state) => state.ui.activeStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const resetWorkspace = useProjectStore((state) => state.resetStore);

  const [notificationsEnabled, setNotificationsEnabled] = usePersistentBoolean('luma-pref-notifications', true);
  const [betaTipsEnabled, setBetaTipsEnabled] = usePersistentBoolean('luma-pref-beta-tips', false);
  const [autoSuffixRotation, setAutoSuffixRotation] = usePersistentBoolean('luma-pref-auto-suffix', false);
  const [lastCleared, setLastCleared] = useState<number | null>(null);

  const handleClearWorkspace = () => {
    resetWorkspace();
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('luma-project-storage');
      window.localStorage.removeItem('luma-generation-state');
    }
    setLastCleared(Date.now());
  };

  return (
    <SettingsLayout
      title="Workspace Settings"
      subtitle="Personalize how the factory feels for you. Theme, default views, and local preferences live here—LLM credentials are now managed by administrators."
      icon={<Users className="w-7 h-7" />}
      badge="Basic"
      actions={
        <div className="flex flex-wrap gap-3">
          {currentUser?.role === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              className="w-40"
              onClick={() => navigate('/admin/settings')}
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Admin Settings
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-40 text-slate-700 hover:text-slate-900 bg-slate-100 border border-slate-200 hover:bg-slate-50"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Builder
          </Button>
        </div>
      }
    >
      {/* Profile */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase font-semibold text-slate-500 tracking-wide">Active User</p>
            <div className="flex items-center gap-3 mt-1">
              <h2 className="text-2xl font-semibold text-slate-900">
                {currentUser?.displayName || 'No user selected'}
              </h2>
              {currentUser && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-700">
                  {currentUser.role === 'admin' ? 'Admin' : 'Builder'}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600">
              {currentUser?.email || 'Set an email when you create the profile to send workspace notifications.'}
            </p>
          </div>
          <div className="text-sm text-slate-500">
            Switch users from the upper-right avatar menu in the main workspace.
          </div>
        </div>
      </section>

      {/* Workspace Preferences */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Palette className="w-5 h-5 text-slate-500" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Workspace Preferences</h3>
            <p className="text-sm text-slate-600">
              Control how the designer opens and how content is grouped.
            </p>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="border border-slate-200 rounded-lg p-4">
            <p className="text-sm font-medium text-slate-900 mb-1">Theme</p>
            <p className="text-xs text-slate-500 mb-3">Pick the base appearance you’re most comfortable with.</p>
            <div className="flex gap-3">
              {(['light', 'dark'] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setTheme(option)}
                  className={cn(
                    'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition',
                    theme === option
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 text-slate-700 hover:border-slate-400'
                  )}
                >
                  {option === 'light' ? 'Light mode' : 'Dark mode'}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg p-4">
            <p className="text-sm font-medium text-slate-900 mb-1">Explorer grouping</p>
            <p className="text-xs text-slate-500 mb-3">Choose how the explorer sidebar clusters artifacts.</p>
            <div className="flex gap-3">
              <button
                className={cn(
                  'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition',
                  explorerGroupBy === 'type'
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 text-slate-700 hover:border-slate-400'
                )}
                onClick={() => setExplorerGroupBy('type')}
              >
                Group by type
              </button>
              <button
                className={cn(
                  'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition',
                  explorerGroupBy === 'none'
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 text-slate-700 hover:border-slate-400'
                )}
                onClick={() => setExplorerGroupBy('none')}
              >
                Flat list
              </button>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <p className="text-sm font-medium text-slate-900 mb-1">Default stage tab</p>
          <p className="text-xs text-slate-500 mb-4">
            This tab opens first whenever you select a project or artifact.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {stageOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setActiveStage(option.id)}
                className={cn(
                  'border rounded-lg p-3 text-left transition',
                  activeStage === option.id
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 hover:border-slate-400'
                )}
              >
                <p className="text-sm font-semibold">{option.label}</p>
                <p className={cn('text-xs mt-1', activeStage === option.id ? 'text-slate-200' : 'text-slate-500')}>
                  {option.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Notifications + tips */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-slate-500" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Notifications & nudges</h3>
            <p className="text-sm text-slate-600">These settings only impact your browser.</p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center justify-between border border-slate-200 rounded-lg p-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Reminders when AI actions finish</p>
              <p className="text-xs text-slate-500">
                Surface toast toasts after plan, script, and BPMN pipelines complete.
              </p>
            </div>
            <input
              type="checkbox"
              checked={notificationsEnabled}
              onChange={(e) => setNotificationsEnabled(e.target.checked)}
              className="w-5 h-5 text-slate-900 accent-slate-900"
            />
          </label>

          <label className="flex items-center justify-between border border-slate-200 rounded-lg p-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Show beta feature prompts</p>
              <p className="text-xs text-slate-500">
                Highlight experimental editors and queue processors as they land.
              </p>
            </div>
            <input
              type="checkbox"
              checked={betaTipsEnabled}
              onChange={(e) => setBetaTipsEnabled(e.target.checked)}
              className="w-5 h-5 text-slate-900 accent-slate-900"
            />
          </label>

          <label className="flex items-center justify-between border border-slate-200 rounded-lg p-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Suggest suffix rotation reminders</p>
              <p className="text-xs text-slate-500">
                When enabled, you’ll get a nudge after several exports reuse the same suffix.
              </p>
            </div>
            <input
              type="checkbox"
              checked={autoSuffixRotation}
              onChange={(e) => setAutoSuffixRotation(e.target.checked)}
              className="w-5 h-5 text-slate-900 accent-slate-900"
            />
          </label>
        </div>
      </section>

      {/* Data management */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <RefreshCcw className="w-5 h-5 text-slate-500" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Local data & cache</h3>
            <p className="text-sm text-slate-600">
              Useful if you need to reset the workspace UI without touching the shared database.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={handleClearWorkspace}
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            Reset cached workspace state
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.localStorage.clear();
              }
              resetWorkspace();
              setLastCleared(Date.now());
            }}
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Clear browser storage
          </Button>
        </div>

        {lastCleared && (
          <p className="text-xs text-slate-500">
            Last cleared {new Date(lastCleared).toLocaleString()}.
          </p>
        )}
      </section>

      {/* Admin notice */}
      {currentUser?.role === 'admin' && (
        <section className="bg-white border border-amber-200 rounded-xl shadow-sm p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-amber-100 text-amber-700">
              <Info className="w-5 h-5" />
            </div>
            <div className="flex-1 space-y-2">
              <h3 className="text-lg font-semibold text-slate-900">AI configuration moved to Admin Settings</h3>
              <p className="text-sm text-slate-600">
                LLM API keys, provider defaults, and models now live under Admin Settings so we can keep them encrypted and shared safely.
                If you need a new provider or model, ask an administrator to update the system settings.
              </p>
              <Button size="sm" onClick={() => navigate('/admin/settings')}>
                <ShieldCheck className="w-4 h-4 mr-2" />
                Manage LLM providers
              </Button>
            </div>
          </div>
        </section>
      )}
    </SettingsLayout>
  );
};
